import React, { useState, useRef, useEffect } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import * as Yup from "yup";
import { useFormik } from "formik";
import { errorToast, successToast } from "../../helpers";
import { UploadMedia } from "../../utils/uploadMedia";
import Loader from "../../helpers/Loader";
import { Close } from "@mui/icons-material";
import { useUpdateFormsMutation } from "../../services/forum";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import {
  useAddBannerMutation,
  useLazyGetBannerByIdQuery,
  useUpdateBannerMutation,
} from "../../services/banner";
import { isValidInput } from "../../utils/validation";

// Required dimensions
const requiredWidth = 343;
const requiredHeight = 152;

const AddBanner = () => {
  const navigate = useNavigate();
  const { _id } = useParams();
  const [error, setError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dimensionError, setDimensionError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [updateBanner] = useUpdateBannerMutation();
  const [addBanner] = useAddBannerMutation();
  const [getById] = useLazyGetBannerByIdQuery();

  const [bannerData, setBannerData] = useState<any>();

  const formik = useFormik({
    initialValues: {
      image: bannerData?.image || "",
      title: bannerData?.title || "",
      btnTitle: bannerData?.buttonText || "",
      description: bannerData?.description || "",
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required("Title is required"),
      btnTitle: Yup.string().required("Button text is required"),
      description: Yup.string().required("Description is required"),
      image: Yup.string().required("Image is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) {
        setError(true);
        return;
      }
      setSubmitting(true);

      const body = {
        title: values.title,
        buttonText: values.btnTitle,
        description: values.description,
        image: values.image,
      };

      try {
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;

        let response: any;
        if (_id) {
          response = await updateBanner({
            body: encryptedBody,
            id: _id,
          }).unwrap();
        } else {
          response = await addBanner({
            body: encryptedBody,
          }).unwrap();
        }

        if (response?.statusCode === 200) {
          successToast(`Banner ${_id ? "updated" : "added"} successfully`);
          navigate("/manage-banner");
        }
      } catch (error: any) {
        console.error("Error saving banner:", error);
        errorToast(error?.data?.message || "");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchData = async () => {
    try {
      // setIsLoading(true);
      const response = await getById({
        id: _id,
      }).unwrap();
      if (response?.statusCode === 200) {
        setBannerData(response?.data);
        formik.setFieldValue("image", response?.data?.image);
        formik.setFieldValue("btnTitle", response?.data?.buttonText);
        formik.setFieldValue("title", response?.data?.title);
        formik.setFieldValue("description", response?.data?.description);
      }
    } catch (error) {
      console.error(error);
    } finally {
      // setIsLoading(false);
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const file = files[0];

      // Check image dimensions before uploading
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = async () => {
        const width = img.width;
        const height = img.height;

        if (width !== requiredWidth || height !== requiredHeight) {
          setDimensionError(
            `Image must be exactly ${requiredWidth}px wide and ${requiredHeight}px tall`
          );
          URL.revokeObjectURL(objectUrl);
          setIsUploading(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        }

        setDimensionError("");

        // Proceed with upload if dimensions are correct
        const res = await UploadMedia(file);
        if (res?.statusCode === 200) {
          formik.setFieldValue("image", res?.data?.image);
        } else {
          errorToast(res?.message || "Error uploading image");
        }

        URL.revokeObjectURL(objectUrl);
        setIsUploading(false);
      };

      img.onerror = () => {
        errorToast("Invalid image file");
        URL.revokeObjectURL(objectUrl);
        setIsUploading(false);
      };

      img.src = objectUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      errorToast("Error uploading image. Please try again.");
      setIsUploading(false);
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleRemoveImage = () => {
    formik.setFieldValue("image", "");
    setDimensionError("");
  };

  useEffect(() => {
    fetchData();
  }, [_id]);

  return (
    <MainContainer>
      <div className="main_loyout">
        <Card className="cards">
          <div className="dashboard">
            <h1 className="mn_hdng">{_id ? "Edit Banner" : "Add Banner"}</h1>
            <Loader isLoad={formik.isSubmitting || isUploading} />
            <Button
              className="btn btn_primary"
              onClick={() => navigate("/manage-banner")}
            >
              Back
            </Button>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography className="custom_label">
                    Image (width: {requiredWidth}px, height: {requiredHeight}px)
                  </Typography>

                  {formik.values.image && (
                    <Box sx={{ mb: 2, maxWidth: 343 }}>
                      <Box sx={{ position: "relative" }}>
                        <CardMedia
                          component="img"
                          image={formik.values.image}
                          alt="banner-image"
                          sx={{
                            height: 152,
                            width: 343,
                            objectFit: "cover",
                            borderRadius: 1,
                          }}
                        />
                        <IconButton
                          sx={{
                            position: "absolute",
                            top: 5,
                            right: 5,
                            backgroundColor: "rgba(0,0,0,0.5)",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.7)",
                            },
                          }}
                          onClick={handleRemoveImage}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}

                  {!formik.values.image && (
                    <Box className="upload_image upload_image_banner">
                      <label htmlFor="icon-button-file">
                        <input
                          style={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          accept="image/png,image/jpeg"
                          onChange={handleImageUpload}
                          disabled={isUploading}
                          ref={fileInputRef}
                        />
                        <Button component="span" className="upload_image_btn">
                          <img
                            style={{
                              height: 152,
                              width: 343,
                              borderRadius: 1,
                            }}
                            src="/static/images/logo.png"
                            alt=""
                          />
                          <CameraAltIcon />
                        </Button>
                      </label>
                    </Box>
                  )}
                  {dimensionError && (
                    <Typography color="error" variant="caption" display="block">
                      {dimensionError}
                    </Typography>
                  )}
                  {error && formik.touched.image && formik.errors.image && (
                    <Typography color="error" variant="caption">
                      {/* {formik.errors.image} */}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Typography className="custom_label">Title</Typography>
                  <TextField
                    className="text_field"
                    hiddenLabel
                    type={"text"}
                    name="title"
                    variant="outlined"
                    fullWidth
                    placeholder="Title"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length === 1 && value === " ") return;
                      formik.handleChange(e);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.title}
                    helperText={
                      formik.touched.title && formik.errors.title
                        ? formik.errors.title.toString()
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Typography className="custom_label">Button Text</Typography>
                  <TextField
                    className="text_field"
                    hiddenLabel
                    type={"text"}
                    name="btnTitle"
                    variant="outlined"
                    fullWidth
                    placeholder="Button Text"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length === 1 && value === " ") return;
                      formik.handleChange(e);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.btnTitle}
                    helperText={
                      formik.touched.btnTitle && formik.errors.btnTitle
                        ? formik.errors.btnTitle.toString()
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}></Grid>
                <Grid item xs={12}>
                  <Typography className="custom_label">Description</Typography>
                  <TextField
                    className="text_field"
                    hiddenLabel
                    type={"text"}
                    name="description"
                    multiline
                    minRows={2}
                    maxRows={4}
                    variant="outlined"
                    fullWidth
                    placeholder="Description"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length === 1 && value === " ") return;
                      formik.handleChange(e);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.description}
                    helperText={
                      formik.touched.description && formik.errors.description
                        ? formik.errors.description.toString()
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <div className="form_btn">
                <Button
                  size="large"
                  type="submit"
                  className="btn btn_primary"
                  onClick={() => setError(true)}
                  disabled={isUploading}
                >
                  {_id ? "Update" : "Submit"}
                </Button>
              </div>
            </CardContent>
          </form>
        </Card>
      </div>
    </MainContainer>
  );
};

export default AddBanner;
